import React, { createContext, useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
  ChatBubble,
  ChatBubbleActionItem,
  ChatBubbleActionList,
  ChatBubbleContent,
  ChatBubbleFooter,
  ChatBubbleHeader,
  ChatBubbleMessageCitationsContext,
  ChatBubbleText,
  ChatBubbleTextInput,
  ChatBubbleTimestamp,
  ChatBubbleWebSources
} from "./ChatBubble";
import ChatFeedback from "./ChatFeedback";
import ChatBubbleImage from "./ChatBubbleImage";
import ChatBubbleFileCard from "./ChatBubbleFileCard";
import ChatBubbleReport from "./ChatBubbleReport";
import ReadAloudActionItem from "./ReadAloudActionItem";
import { showCommenter, showErrorMessage } from "@minohealth/common/utils";
import useEventListener from "@minohealth/common/hooks/useEventListener";
import { useDeepinfraTTS } from "../../services/tts/useDeepinfraTTS";
import { BASE_AGENT_ID, BASE_DEEPRESEARCH_AGENT_ID, computeAgentBubbleData, formatMarkdownForCopy, formatMarkdownForCopyHtml, normaliseChatMessageParts } from "./utils";
import ChatBubbleProteinFileCard, { isProteinFile } from "./ChatBubbleProteinFileCard";
import ChatBubbleTool from "./ChatBubbleTool";
import ChatBubbleToolsCollapsible from "./ChatBubbleToolsCollapsible";

import { MdContentCopy, MdEdit, MdClose } from 'react-icons/md'
import { BsCheck } from 'react-icons/bs'
import { BiLike, BiDislike, BiSolidLike, BiSolidDislike } from "react-icons/bi";
import { FaRegCommentDots } from "react-icons/fa";
import { IoMdShareAlt } from "react-icons/io";
import { HiRefresh } from "react-icons/hi";
import { RiExternalLinkLine } from "react-icons/ri";
import ChatBubbleReportCard from "./ChatBubbleReportCard";
import { CanvasSuggestionsButton, extractCanvasDocumentId } from "./ChatBubbleCanvas";
import ChatBubbleReasoning from "./ChatBubbleReasoning";
import ChatBubbleImageSearchResults from "./ChatBubbleImageSearchResults";
import { ChatBubbleLoader } from "./ChatBubbleLoader";
import { ReviewProgressLine } from "./ReviewProgressLine";
import { useReviewProgress } from "@/services/chat-provider/canvas/reviewProgress";
import ChatBubbleDeepResearch from "./ChatBubbleDeepResearch";
import { AGENT_USER, AgentMode, AI_USER, DEEP_RESEARCH_USER, isStartingMessageEditEvent, STARTING_MESSAGE_EDIT_EVENT, useChatContext } from "@/services/chat-provider";

/**
 * @typedef {Object} ChatMessageBubbleProps
 * @property {ChatMessage} message
 * @property {ChatMessage[]} messages
 * @property {(data: Partial<Pick<ChatMessage, "harmful" | "inaccurate" | "rating" | "isGood" | "isBad" | "comment">>) => void} sendFeedback
 * @property {(srcImageId: string, bboxes?: Array<*>) => void} previewImage
 * @property {() => void} openReportFormForAgentResponse
 * @property {(reportSource: import('../chat-report-panel').ReportSource) => void} previewReport
 * @property {(document: object) => void} shareDocument
 * @property {(text: string) => void} editMessage
 * @property {() => void} regenerateMessage
 * @property {number} messageIndex
 * @property {number} messagesLength
 * @property {boolean} isLatestMessage
 * @property {boolean} [isStreaming]
 */

/**
 * Custom equality check — callbacks are excluded intentionally.
 * They are recreated each render in ChatContent but their behaviour never
 * changes between renders, so skipping them is safe and avoids a full
 * list re-render on every SSE streaming chunk.
 * @param {ChatMessageBubbleProps} prev
 * @param {ChatMessageBubbleProps} next
 */
function chatMessageBubbleAreEqual(prev, next) {
  return (
    prev.message === next.message &&
    prev.messages === next.messages &&
    prev.isStreaming === next.isStreaming &&
    prev.messageIndex === next.messageIndex &&
    prev.messagesLength === next.messagesLength &&
    prev.isLatestMessage === next.isLatestMessage
  )
}

/** @param {ChatMessageBubbleProps} props */
function ChatMessageBubble({ message, messages, sendFeedback, previewImage, previewReport, openReportFormForAgentResponse, shareDocument, editMessage, regenerateMessage, messageIndex, messagesLength, isLatestMessage, isStreaming = false }) {
  const { useActiveChatSessionReports } = useChatContext()

  const isAiChatBubble = message.user === AI_USER
  const isAgentChatBubble = (
    message.user === AGENT_USER ||
    message.user === DEEP_RESEARCH_USER
  )

  if (isAiChatBubble) {
    return (
      <AiChatBubble
        message={message}
        messages={messages}
        sendFeedback={sendFeedback}
        previewImage={previewImage}
        previewReport={previewReport}
        openReportFormForAgentResponse={openReportFormForAgentResponse}
        shareDocument={shareDocument}
        regenerateMessage={regenerateMessage}
        messageIndex={messageIndex}
        messagesLength={messagesLength}
        isLatestMessage={isLatestMessage}
        isStreaming={isStreaming}
      />
    )
  }

  if (isAgentChatBubble) {
    return (
      <AgentChatBubble
        message={message}
        messages={messages}
        sendFeedback={sendFeedback}
        previewImage={previewImage}
        previewReport={previewReport}
        shareDocument={shareDocument}
        openReportFormForAgentResponse={openReportFormForAgentResponse}
        regenerateMessage={regenerateMessage}
        messageIndex={messageIndex}
        messagesLength={messagesLength}
        isLatestMessage={isLatestMessage}
        isStreaming={isStreaming}
      />
    )
  }

  return (
    <UserChatBubble
      message={message}
      previewImage={previewImage}
      editMessage={editMessage}
    />
  )
}

/** @param {Pick<ChatMessageBubbleProps, "message" | "previewImage" | "editMessage">} props */
function UserChatBubble({ message, previewImage, editMessage }) {
  const [isEditting, setIsEditting] = useState(false)

  /** @ts-ignore */
  useEventListener(document, STARTING_MESSAGE_EDIT_EVENT, event => {
    if (!isStartingMessageEditEvent(event)) return
    setIsEditting(event.detail.messageId === message._id)
  })

  function startEditingMessage() {
    document.dispatchEvent(
      new CustomEvent(STARTING_MESSAGE_EDIT_EVENT, {
        detail: { messageId: message._id }
      })
    )
  }

  /** @param {string} text */
  function saveEdittedMessage(text) {
    editMessage(text)
    setIsEditting(false)
  }

  function cancelMessageEdit() {
    setIsEditting(false)
  }

  const canEditMessage = message._id != null
  const editFormId = `chat-edit-form-${message._id}`

  return (
    <div className="chat-bubble-user-wrapper">
      <ChatBubble variant="user" data-editting={isEditting}>
        <ChatBubbleContent>
          {!isEditting && (
            <ChatBubbleText
              shouldDisplayMarkdown={false}
              messageText={message.text}
            />
          )}
          {isEditting && (
            <ChatBubbleTextInput
              defaultValue={message.text}
              onSave={saveEdittedMessage}
              onCancel={cancelMessageEdit}
              formId={editFormId}
            />
          )}

          {message.images?.map((img, idx) => (
            <ChatBubbleImage
              key={idx}
              messageText={message.text}
              messageImage={img}
              previewImage={previewImage}
            />
          ))}
          {message.files?.map((file, idx) => (
            <ChatBubbleFileCard key={idx} messageFile={file} />
          ))}
        </ChatBubbleContent>
      </ChatBubble>

      <ChatBubbleActionList
        className="chat-bubble__action-list chat-bubble-user-actions"
        data-visible={isEditting}
      >
        {!isEditting ? (
          <>
            <CopyMessageActionItem getMessage={() => message.text} />
            <EditMessageActionItem
              canEdit={canEditMessage}
              isEditing={isEditting}
              onEditMessage={startEditingMessage}
            />
          </>
        ) : (
          <>
            <ChatBubbleActionItem
              title="Cancel"
              onClick={() => document.getElementById(editFormId)?.reset()}
            >
              <MdClose />
            </ChatBubbleActionItem>
            <ChatBubbleActionItem
              title="Save"
              onClick={() => document.getElementById(editFormId)?.requestSubmit()}
            >
              <BsCheck />
            </ChatBubbleActionItem>
          </>
        )}
      </ChatBubbleActionList>
    </div>
  )
}

/** @param {Omit<ChatMessageBubbleProps, "editMessage">} props */
function AiChatBubble({ message, sendFeedback, previewImage, previewReport, shareDocument, regenerateMessage, messageIndex, isLatestMessage, isStreaming }) {
  const hasReport = message.report != null

  const getMessageMarkdownToCopy = useCallback(
    () => message.text,
    [message.text]
  )

  const canRegenerateResponse = !isStreaming && !hasReport

  // Check if we should show the loader: streaming with no content or only empty text
  const shouldShowLoader = isStreaming && (
    message.chunks == null ||
    message.chunks.length === 0 ||
    message.chunks.every(chunk => chunk.trim() === "")
  )

  // Import TTS hook for proactive generation and sharing with ReadAloudActionItem
  const ttsHook = useDeepinfraTTS({
    voice: "tara",
    enabled: true,
    debug: true,
  });

  // Track if we've already triggered proactive generation for this message
  const proactiveTriggeredRef = useRef(false);

  // Reset the trigger when message changes
  useEffect(() => {
    proactiveTriggeredRef.current = false;
  }, [message._id]);

  // Proactively generate first sentence when message is complete (ONLY for latest message)
  useEffect(() => {
    if (!isStreaming && message.text && message.text.trim().length > 0 && isLatestMessage) {
      // Small delay to ensure message is fully rendered, then start proactive generation
      const timer = setTimeout(() => {
        ttsHook.generateProactiveFirstSentence(message.text);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [message.text, isStreaming, isLatestMessage, ttsHook.generateProactiveFirstSentence]);

  // ENHANCED: Aggressive proactive TTS generation during streaming for instant playback (ONLY for latest message)
  useEffect(() => {
    if (!isLatestMessage) return; // Early return for non-latest messages

    const currentText = message.text || "";

    if (currentText && currentText.length > 0 && !proactiveTriggeredRef.current) {
      // AGGRESSIVE APPROACH: Start generation as soon as we have meaningful content

      // Method 1: Look for complete sentences first (most reliable)
      const completeSentenceMatch = currentText.match(/^[^.!?]*[.!?]+/);
      if (completeSentenceMatch && completeSentenceMatch[0].trim().length > 20) {
        proactiveTriggeredRef.current = true;
        ttsHook.generateProactiveFirstSentence(currentText);
        return;
      }

      // Method 2: Look for natural breaks (colons, semicolons) 
      const naturalBreakMatch = currentText.match(/^[^:;]{25,}[;:]/);
      if (naturalBreakMatch && naturalBreakMatch[0].trim().length > 30) {
        proactiveTriggeredRef.current = true;
        ttsHook.generateProactiveFirstSentence(currentText);
        return;
      }

      // Method 3: For streaming content, start generation with substantial partial content
      if (currentText.length >= 60) {
        const words = currentText.trim().split(/\s+/);
        if (words.length >= 10) { // At least 10 words for meaningful content
          proactiveTriggeredRef.current = true;
          ttsHook.generateProactiveFirstSentence(currentText);
          return;
        }
      }
    }
  }, [message.text, isStreaming, isLatestMessage, ttsHook.generateProactiveFirstSentence]);

  return (
    <ChatBubble variant="ai" hasReport={hasReport}>
      {/* <ChatBubbleHeader>
      </ChatBubbleHeader> */}

      <ChatBubbleContent>
        <ChatBubbleText
          shouldDisplayMarkdown
          messageText={message.text}
          isStreaming={isStreaming}
        />
        {message.images?.map((img, idx) => (
          <ChatBubbleImage
            key={idx}
            messageText={message.text}
            messageImage={img}
            previewImage={previewImage}
          />
        ))}

        <ChatBubbleReport
          messageReport={message.report}
          previewReport={previewReport}
          isStreaming={isStreaming}
        />

        {shouldShowLoader && <ChatBubbleLoader />}
      </ChatBubbleContent>

      {!isStreaming && (
        <ChatBubbleFooter>
          {/* <ChatFeedback message={message} sendFeedback={sendFeedback} /> */}

          <ChatBubbleActionList>
            {!hasReport && (
              <CopyMessageActionItem getMessage={getMessageMarkdownToCopy} />
            )}
            {!hasReport && (
              <ReadAloudActionItem
                isStreamingMessage={isStreaming}
                messageText={message.text}
                ttsHook={ttsHook}
              />
            )}
            <GoodResponseActionItem
              isMessageGood={message.isGood}
              sendFeedback={sendFeedback}
            />
            <BadResponseActionItem
              isMessageBad={message.isBad}
              sendFeedback={sendFeedback}
            />
            <CommentActionItem
              messageComment={message.comment}
              sendFeedback={sendFeedback}
            />
            <ReportSharingActionItem
              messageReport={message.report}
              shareDocument={shareDocument}
            />
            {canRegenerateResponse && (
              <RegenerateMessageActionItem
                regenerateMessage={regenerateMessage}
              />
            )}
          </ChatBubbleActionList>

          <ChatBubbleTimestamp timestamp={message.timestamp} />
        </ChatBubbleFooter>
      )}
    </ChatBubble>
  )
}

const INCOMPLETE_TOOL_RESULT = new Error("No result")
const CANVAS_TOOL_NAMES = new Set(["create_canvas", "edit_document", "write_document"])


/** @param {Omit<ChatMessageBubbleProps, "editMessage">} props */
function AgentChatBubble({ message, messages, sendFeedback, previewImage, previewReport, openReportFormForAgentResponse, shareDocument, regenerateMessage, messageIndex, messagesLength, isLatestMessage, isStreaming: isStreamingMessage }) {
  const { normalisedParts, messageCitations, allMessageReports, imageSearchResults, allGeneratedFiles } = computeAgentBubbleData(message)
  const { useActiveChatSessionId } = useChatContext()
  const [activeChatSessionId] = useActiveChatSessionId()
  // A canvas review reports its progress while it streams; other agents use the dot loader.
  const reviewProgress = useReviewProgress(isStreamingMessage ? activeChatSessionId : null)
  // The text is done here. The stream stays open a few seconds more to save and bill the run.
  const isTextDone = message.message?.some(part => part.event === "RunCompleted") ?? false
  const isLastMessage = messageIndex === messagesLength - 1

  const currentImagesInContext = (() => {
    for (let i = messageIndex; i >= 0; i--) {
      if (messages[i]?.images?.length > 0) return messages[i].images
    }
    return []
  })()

  // canvas_agent (Moremi Reviewer) is a standalone agent, not part of the "team" —
  // its narrative text carries an agent_id outside the team/deepresearch whitelist below,
  // so it must be let through unconditionally (same reasoning as the disabled agent_id
  // check on toolCallParts above, which hit this same class of bug for tool calls).
  // A plain answer has no canvas tool call, so the message's mode decides; the tool
  // check covers messages saved without one.
  const isCanvasReviewerMessage = message.meta?.mode === AgentMode.CANVAS_AGENT || normalisedParts.some(part => (
    part.type === "tool_call" && CANVAS_TOOL_NAMES.has(part.data.name)
  ))
  const leadTextParts = normalisedParts.filter(part => (
    part.type === "text" &&
    (
      isCanvasReviewerMessage ||
      part.agent_id == null || part.agent_id === BASE_AGENT_ID || part.agent_id === BASE_DEEPRESEARCH_AGENT_ID
    )
  ))
  // A finished reply is never shown empty because its agent id is unknown.
  const textResponseParts = leadTextParts.length > 0 || isStreamingMessage
    ? leadTextParts
    : normalisedParts.filter(part => part.type === "text")
  const hasTextContent = textResponseParts.some(part => part.data.text.trim() !== "")
  const shouldShowLoader = isStreamingMessage

  const hasReportGenerationTag = normalisedParts.some(part => part.type === "report_generation_tag")
  const canRegenerateResponse = !isStreamingMessage

  const getMessageMarkdownToCopy = useCallback(() => {
    const body = textResponseParts.map(part => part.data.text).join('')
    const citations = messageCitations.length > 0
      ? "\n\n" + messageCitations.map(citation => `1. ${citation.url}`).join('\n')
      : ""
    return body + citations
  }, [textResponseParts, messageCitations])

  // Raw joined text for TTS — markdown formatting not needed for speech
  const textForReadAloud = useMemo(
    () => textResponseParts.map(part => part.data.text).join(''),
    [textResponseParts]
  )

  // Import TTS hook for proactive generation and sharing with ReadAloudActionItem
  const ttsHook = useDeepinfraTTS({
    voice: "tara",
    enabled: true,
    debug: true,
  });

  // Track if we've already triggered proactive generation for this agent message
  const agentProactiveTriggeredRef = useRef(false);

  // Reset the trigger when message changes
  useEffect(() => {
    agentProactiveTriggeredRef.current = false;
  }, [message._id]);

  // Proactively generate first sentence when agent message is complete (ONLY for latest message)
  useEffect(() => {
    if (!isStreamingMessage && textForReadAloud && textForReadAloud.trim().length > 0 && !agentProactiveTriggeredRef.current && isLatestMessage) {
      agentProactiveTriggeredRef.current = true;
      ttsHook.generateProactiveFirstSentence(textForReadAloud);
    }
  }, [textForReadAloud, isStreamingMessage, isLatestMessage, ttsHook.generateProactiveFirstSentence]);

  // NEW: Proactively generate first sentence during agent streaming (ONLY for latest message)
  useEffect(() => {
    if (!isLatestMessage) return; // Early return for non-latest messages

    if (isStreamingMessage && message.message && message.message.length > 0) {
      // Get text from streaming chunks (agent messages use messageChunks)
      const textParts = normaliseChatMessageParts(message.message)
        .filter(part => part.type === "text")
        .map(part => part.data.text);

      const currentText = textParts.join("");

      // Check if we have a complete first sentence
      const firstSentenceMatch = currentText.match(/^[^.!?:;]*[.!?:;]/);

      if (firstSentenceMatch) {
        const firstSentence = firstSentenceMatch[0].trim();

        // Only generate if sentence is substantial and we haven't triggered yet
        if (firstSentence.length > 20 && !agentProactiveTriggeredRef.current) {
          agentProactiveTriggeredRef.current = true;

          // Call immediately - no setTimeout, no delays, no race conditions
          ttsHook.generateProactiveFirstSentence(firstSentence);
        }
      }
    }
  }, [message.message, isStreamingMessage, isLatestMessage, ttsHook.generateProactiveFirstSentence]);

  const toolCallParts = (() => {
    if(message.user === DEEP_RESEARCH_USER) return []
    return normalisedParts.filter(part => (
      part.type === "tool_call"
      // part.type === "tool_call" &&
      // (part.agent_id == null || part.agent_id === BASE_AGENT_ID)
    ))
  })()
  const doneToolCallParts = toolCallParts.filter(part => {
    if (part.type === "tool_call" && part.data.result != null) return true
    // ! Hack for completed toolcalls without results
    if(!isStreamingMessage) return true
    const partIndex = normalisedParts.findIndex(normalisedPart => normalisedPart === part)
    return normalisedParts.slice(partIndex + 1).some(part => part.type === "text")
  }).map(part => {
    if (part.type === "tool_call" && part.data.result != null) return part
    return {
      ...part,
      data: {
        ...part.data,
        result: INCOMPLETE_TOOL_RESULT,
      },
    }
  })
  const ongoingToolCallParts = toolCallParts.filter(part => {
    if (part.type === "tool_call" && part.data.result != null) return false
    // ! Hack for completed toolcalls without results
    if(!isStreamingMessage) return false
    const partIndex = normalisedParts.findIndex(normalisedPart => normalisedPart === part)
    return !normalisedParts.slice(partIndex + 1).some(part => part.type === "text")
  })

  const proteinFiles = allGeneratedFiles.filter(f => isProteinFile(f.url))

  return (
    <ChatBubble variant="ai" hasReport={false}>

      <ChatBubbleContent>
        {/* On top, so the live reasoning below cannot push it out of view. */}
        {shouldShowLoader && reviewProgress && <ReviewProgressLine progress={reviewProgress} />}
        <ChatBubbleMessageCitationsContext value={{ citations: messageCitations }}>
          <ChatBubbleReasoning
            normalisedParts={message.user !== DEEP_RESEARCH_USER ? normalisedParts: []}
            isStreaming={isStreamingMessage && !isTextDone}
          />

          {message.user === DEEP_RESEARCH_USER && (
            <ChatBubbleDeepResearch
              normalisedParts={normalisedParts}
              isStreaming={isStreamingMessage}
            />
          )}

          {doneToolCallParts.length > 0 && (
            <ChatBubbleToolsCollapsible toolCount={doneToolCallParts.length} className="mb-4">
              {doneToolCallParts.map((part, index) => (
                <ChatBubbleTool
                  name={part.data.name}
                  args={part.data.args}
                  result={part.data.result}
                  key={JSON.stringify(part.data) + index}
                />
              ))}
            </ChatBubbleToolsCollapsible>
          )}

          <ChatBubbleImageSearchResults imageResults={imageSearchResults} />

          {/* Keyed by position: a key that holds the text mounts the part again on every
              streamed chunk, which starts each chart's animation again. */}
          {textResponseParts.map((part, index) => (
            <ChatBubbleText
              shouldDisplayMarkdown
              messageText={part.data.text}
              key={index}
            />
          ))}

          <div className="mb-3">
            {ongoingToolCallParts.map((part, index) => (
              <div className="w-fit">
                <ChatBubbleTool
                  name={part.data.name}
                  args={part.data.args}
                  result={part.data.result}
                  key={JSON.stringify(part.data) + index}
                />
              </div>
            ))}
          </div>
        </ChatBubbleMessageCitationsContext>

        {!isStreamingMessage && (() => {
          const canvasDocumentId = doneToolCallParts
            .filter(part => CANVAS_TOOL_NAMES.has(part.data.name))
            .map(part => extractCanvasDocumentId({ name: part.data.name, args: part.data.args, result: part.data.result }))
            .findLast(id => id != null)
          return <CanvasSuggestionsButton documentId={canvasDocumentId} />
        })()}

        {(!isStreamingMessage && allMessageReports.length > 0) && (
          <div className="mb-4">
            {allMessageReports.map(report => (
              <ChatBubbleReportCard
                reportImages={currentImagesInContext}
                report={report}
                previewReport={previewReport}
                key={report.content}
              />
            ))}
          </div>
        )}

        {(!isStreamingMessage && proteinFiles.length > 0) && (
          <div className="mb-4">
            {proteinFiles.map(file => (
              <ChatBubbleProteinFileCard
                key={file.url}
                files={proteinFiles}
                defaultFile={file}
              />
            ))}
          </div>
        )}

        <ChatBubbleWebSources
          messageCitations={messageCitations}
          isStreamingMessage={isStreamingMessage}
        />

        {(!isStreamingMessage && hasReportGenerationTag && isLastMessage) && (
          <div className="grid justify-center mb-4">
            <button
              type="button"
              className="flex items-center gap-2 px-6 py-4 rounded-lg font-bold bg-transparent border border-gray-300/20 transition-colors duration-150 hover:bg-gray-400/10 focus-visible:bg-gray-400/10"
              onClick={() => openReportFormForAgentResponse()}
            >
              <span>Fill Report Form</span>
              <RiExternalLinkLine className="text-2xl" />
            </button>
          </div>
        )}

        {shouldShowLoader && !reviewProgress && <ChatBubbleLoader />}
      </ChatBubbleContent>

      {!isStreamingMessage && (
        <ChatBubbleFooter>
          {/* <ChatFeedback message={message} sendFeedback={sendFeedback} /> */}

          <ChatBubbleActionList>
            <CopyMessageActionItem getMessage={getMessageMarkdownToCopy} />
            <ReadAloudActionItem
              isStreamingMessage={isStreamingMessage}
              messageText={textForReadAloud}
              ttsHook={ttsHook}
            />
            <GoodResponseActionItem
              isMessageGood={message.isGood}
              sendFeedback={sendFeedback}
            />
            <BadResponseActionItem
              isMessageBad={message.isBad}
              sendFeedback={sendFeedback}
            />
            <CommentActionItem
              messageComment={message.comment}
              sendFeedback={sendFeedback}
            />
            {canRegenerateResponse && (
              <RegenerateMessageActionItem
                regenerateMessage={regenerateMessage}
              />
            )}
          </ChatBubbleActionList>

          <ChatBubbleTimestamp timestamp={message.timestamp} />
        </ChatBubbleFooter>
      )}
    </ChatBubble>
  )
}

/** @param {{ getMessage: () => string }} props - getMessage returns the raw markdown source to copy */
function CopyMessageActionItem({ getMessage }) {
  const [isMessageCopied, setIsMessageCopied] = useState(false)

  useEffect(() => {
    if (!isMessageCopied) return

    const timeout = setTimeout(() => {
      setIsMessageCopied(false)
    }, 2000)

    return () => clearTimeout(timeout)
  }, [isMessageCopied])

  async function copyMessage() {
    const markdown = getMessage()
    try {
      // Write both text/html and text/plain so pasting into rich text targets
      // (docs, emails, etc.) preserves formatting the same way a native
      // browser copy-from-selection would, while plain text editors still
      // get a clean plain-text fallback.
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([formatMarkdownForCopyHtml(markdown)], { type: "text/html" }),
            "text/plain": new Blob([formatMarkdownForCopy(markdown)], { type: "text/plain" }),
          }),
        ])
      } else {
        await navigator.clipboard.writeText(formatMarkdownForCopy(markdown))
      }
      setIsMessageCopied(true)
    } catch (err) {
      showErrorMessage(err.message)
    }
  }

  return (
    <ChatBubbleActionItem
      title={isMessageCopied ? 'Copied' : 'Copy'}
      className="chat-bubble__copy-btn"
      data-copied={isMessageCopied}
      onClick={copyMessage}
    >
      {!isMessageCopied && <MdContentCopy />}
      {isMessageCopied && <BsCheck />}
    </ChatBubbleActionItem>
  )
}

/**
 * @param {Object} props
 * @param {boolean=} props.isMessageGood
 * @param {ChatMessageBubbleProps["sendFeedback"]} props.sendFeedback
 */
function GoodResponseActionItem({ isMessageGood, sendFeedback }) {
  return (
    <ChatBubbleActionItem
      title="Good response"
      onClick={() => sendFeedback?.({ isGood: !isMessageGood })}
    >
      {isMessageGood && <BiSolidLike />}
      {!isMessageGood && <BiLike />}
    </ChatBubbleActionItem>
  )
}

/**
 * @param {Object} props
 * @param {boolean=} props.isMessageBad
 * @param {ChatMessageBubbleProps["sendFeedback"]} props.sendFeedback
 */
function BadResponseActionItem({ isMessageBad, sendFeedback }) {
  return (
    <ChatBubbleActionItem
      title="Bad response"
      onClick={() => sendFeedback?.({ isBad: !isMessageBad })}
    >
      {isMessageBad && <BiSolidDislike />}
      {!isMessageBad && <BiDislike />}
    </ChatBubbleActionItem>
  )
}

/**
 * @param {Object} props
 * @param {string=} props.messageComment
 * @param {ChatMessageBubbleProps["sendFeedback"]} props.sendFeedback
 */
function CommentActionItem({ messageComment, sendFeedback }) {
  function openCommentDialog() {
    showCommenter(
      /** @param {string} comment */
      (comment) => {
        sendFeedback({ comment })
      },
      () => {
        if (messageComment != null) return
        sendFeedback({ comment: "" })
      },
      messageComment,
      "Comment",
      "Tell us what you think about this response",
      "Send"
    )
  }

  return (
    <ChatBubbleActionItem
      title="Comment"
      onClick={openCommentDialog}
    >
      <FaRegCommentDots />
    </ChatBubbleActionItem>
  )
}

/**
 * @param {Object} props
 * @param {string} props.messageReport
 * @param {ChatMessageBubbleProps["shareDocument"]} props.shareDocument
 */
function ReportSharingActionItem({ messageReport, shareDocument }) {
  const { useActiveChatSessionReports } = useChatContext()
  const { data: chatReports } = useActiveChatSessionReports()

  const reportObj = chatReports?.find(report => (
    messageReport != null &&
    (report._id === messageReport || report._clientId === messageReport)
  ))

  function openReportSharingDialog() {
    shareDocument(reportObj)
  }

  if (reportObj == null) return null

  return (
    <ChatBubbleActionItem
      title="Share Report"
      onClick={openReportSharingDialog}
    >
      <IoMdShareAlt />
    </ChatBubbleActionItem>
  )
}

/**
 * @param {Object} props
 * @param {ChatMessageBubbleProps["regenerateMessage"]} props.regenerateMessage
 */
function RegenerateMessageActionItem({ regenerateMessage }) {
  return (
    <ChatBubbleActionItem
      title="Regenerate"
      onClick={regenerateMessage}
    >
      <HiRefresh />
    </ChatBubbleActionItem>
  )
}

/**
 * @param {Object} props
 * @param {boolean} props.canEdit - Whether the message can be edited
 * @param {boolean} props.isEditing - Whether the edit form is active
 * @param {() => void} props.onEditMessage - Function to call when edit button is clicked
 */
function EditMessageActionItem({ canEdit, isEditing, onEditMessage }) {
  if (!canEdit) return null;
  if (isEditing) return null;

  return (
    <ChatBubbleActionItem
      className="chat-bubble__edit-btn"
      title="Edit message"
      onClick={onEditMessage}
    >
      <MdEdit />
    </ChatBubbleActionItem>
  );
}

export default React.memo(ChatMessageBubble, chatMessageBubbleAreEqual)
